import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import API_BASE from "../config/api";
import { resolveImageUrl, getInitials } from "../utils/siteHelpers";
import useScrollReveal from "../hooks/useScrollReveal";
import "../styles/reveal.css";
import SiteLoadingState from "../components/site/SiteLoadingState";
import SiteErrorState from "../components/site/SiteErrorState";
import SiteEmptyState from "../components/site/SiteEmptyState";

/* ============================================================
   BLANK defaults — a newly activated site shows its OWN (empty)
   content, never demo data. Real data (get_personal_site.php)
   overrides per-field. (Unchanged from original — do not add
   placeholder copy here, PersonalWebsite relies on it staying blank.)
   ============================================================ */
const DEMO = {
    name: "",
    currentjob: "",
    slogan: "",
    aboutme: "",
    experience: "",
    languages: "",
    mobile: "",
    whatsapp: "",
    email: "",
    addresses: "",
    aboutimg: "",
    coverimg: "",
    images: [],
};

/* What I Do / Expertise cards — filled by the owner (blank by default) */
const SERVICES = [];

/* Testimonials — blank by default */
const TESTIMONIALS = [];

/* Load Bootstrap (grid + navbar collapse + form controls) once, clean up on
   unmount. AOS has been removed in favour of the CSS-only useScrollReveal
   hook (see src/hooks/useScrollReveal.js) — one less external script/style
   dependency, same "animate once on scroll" behaviour. */
function useCdnAssets() {
    useEffect(() => {
        const added = [];
        const addLink = (href) => {
            if (document.querySelector(`link[href="${href}"]`)) return;
            const l = document.createElement("link");
            l.rel = "stylesheet";
            l.href = href;
            l.dataset.pw = "1";
            document.head.appendChild(l);
            added.push(l);
        };
        const addScript = (src) =>
            new Promise((resolve) => {
                if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
                const s = document.createElement("script");
                s.src = src;
                s.async = true;
                s.dataset.pw = "1";
                s.onload = () => resolve();
                document.body.appendChild(s);
                added.push(s);
            });

        addLink("https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css");
        addLink("https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css");
        addLink("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Poppins:wght@500;600;700;800&display=swap");

        addScript("https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/js/bootstrap.bundle.min.js");

        return () => { added.forEach((n) => { try { n.remove(); } catch (e) { /* noop */ } }); };
    }, []);
}

/* Tracks header scroll position so the navbar can pick up a shadow/blur
   once the page has scrolled past the hero — small production-feel detail
   the reference site has. */
function useScrolled(threshold = 24) {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > threshold);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [threshold]);
    return scrolled;
}

/* Drop-in <img> replacement: if `src` is empty or fails to load, renders a
   themed gradient tile with a centered icon instead of a broken image box.
   Resets its own "ok" state whenever the src changes (site reload / new
   domain) so a previous failure doesn't stick around. */
function ImgOrFallback({ src, alt, className = "", icon = "bi-image", loading = "lazy" }) {
    const [ok, setOk] = useState(true);
    useEffect(() => { setOk(true); }, [src]);
    if (src && ok) {
        return <img src={src} alt={alt} loading={loading} className={className} onError={() => setOk(false)} />;
    }
    return (
        <div className={`pw-img-fallback ${className}`} role="img" aria-label={alt || "Image unavailable"}>
            <i className={`bi ${icon}`} aria-hidden="true"></i>
        </div>
    );
}

export default function PersonalWebsite() {
    const { domain } = useParams();
    const [real, setReal] = useState(null);
    const [realImages, setRealImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [imgOk, setImgOk] = useState(true);
    const [coverOk, setCoverOk] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);

    // contact form
    const [cName, setCName] = useState("");
    const [cPhone, setCPhone] = useState("");
    const [cEmail, setCEmail] = useState("");
    const [cMsg, setCMsg] = useState("");
    const [fetchToken, setFetchToken] = useState(0);

    useCdnAssets();
    const scrolled = useScrolled();

    const loadSite = useCallback(() => {
        if (!domain) { setLoading(false); return; }
        setLoading(true);
        setLoadError(false);
        fetch(`${API_BASE}/api/get_personal_site.php?domain=${encodeURIComponent(domain)}`)
            .then((r) => r.json())
            .then((res) => {
                if (res.success && res.data) setReal(res.data);
                if (res.images && res.images.length) setRealImages(res.images);
            })
            .catch(() => setLoadError(true))
            .finally(() => setLoading(false));
    }, [domain]);

    useEffect(() => { loadSite(); }, [loadSite, fetchToken]);

    // CSS-only scroll reveal (replaces AOS) — re-runs once real content mounts.
    useScrollReveal([loading, real, realImages]);

    const pick = (k) => (real && real[k] && String(real[k]).trim() !== "" ? real[k] : DEMO[k]);
    const d = useMemo(() => ({
        name: pick("name"),
        currentjob: pick("currentjob"),
        slogan: pick("slogan"),
        aboutme: pick("aboutme"),
        experience: pick("experience"),
        languages: pick("languages"),
        mobile: pick("mobile"),
        whatsapp: pick("whatsapp"),
        email: pick("email"),
        addresses: pick("addresses"),
        aboutimg: real ? (real.aboutimg || "") : DEMO.aboutimg,
        coverimg: real ? (real.coverimg || "") : DEMO.coverimg,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [real]);
    const images = realImages.length ? realImages : DEMO.images;

    // Image links coming back from the API are a MIX of two shapes:
    //  - relative paths owned by this backend, e.g. "uploads/portfolio/xxx.png"
    //    (need API_BASE prefixed on)
    //  - already-absolute URLs on a separate CDN, e.g.
    //    "https://images.dialnepal.com.np/portfolio/xxx.png" (must be left alone)
    // resolveImageUrl only handles the first case, so blindly passing every
    // value through it double-prefixes the already-absolute ones and breaks
    // them. Detect absolute URLs (http(s):// or protocol-relative //) first
    // and skip resolution for those.
    const imgUrl = useCallback((u) => {
        if (!u) return u;
        if (/^https?:\/\//i.test(u) || u.startsWith("//")) return u;
        return resolveImageUrl(API_BASE, u);
    }, []);
    const photo = imgUrl(d.aboutimg);
    const cover = imgUrl(d.coverimg);
    const hasPhoto = photo && imgOk;
    const initials = getInitials(d.name);
    const firstName = (d.name || "").trim().split(/\s+/)[0] || d.name;
    const langCount = d.languages ? d.languages.split(",").filter(Boolean).length : 0;
    const isSiteEmpty = !d.name && !d.aboutme && images.length === 0;

    // Preload the cover image so the hero background-image (no onError of
    // its own) only gets used once we know it actually resolves — otherwise
    // it falls back to the themed gradient + dot pattern.
    useEffect(() => {
        if (!cover) { setCoverOk(false); return; }
        let cancelled = false;
        const img = new Image();
        img.onload = () => { if (!cancelled) setCoverOk(true); };
        img.onerror = () => { if (!cancelled) setCoverOk(false); };
        img.src = cover;
        return () => { cancelled = true; };
    }, [cover]);

    const handleContact = (e) => {
        e.preventDefault();
        const subject = encodeURIComponent(`Website enquiry from ${cName || "Visitor"}`);
        const body = encodeURIComponent(`Name: ${cName}\nPhone: ${cPhone}\nEmail: ${cEmail}\n\n${cMsg}`);
        window.location.href = `mailto:${d.email}?subject=${subject}&body=${body}`;
    };

    if (loading) return <SiteLoadingState theme="personal" label="Loading site…" />;
    if (loadError) return <SiteErrorState theme="personal" onRetry={() => setFetchToken((t) => t + 1)} />;

    return (
        <div className="pw" data-bs-spy="scroll" data-bs-target="#mainNav" data-bs-offset="80">

            {/* SKIP LINK — keyboard/screen-reader users can bypass the nav */}
            <a href="#main-content" className="pw-skip-link">Skip to content</a>

            {/* NAV */}
            <nav id="mainNav" className={`navbar navbar-expand-lg navbar-light fixed-top py-3 ${scrolled ? "pw-nav-scrolled" : ""}`} aria-label="Primary">
                <div className="container">
                    <a className="navbar-brand fw-bold fs-4 text-dark" href="#top">
                        <i className="bi bi-patch-check-fill text-primary me-2" aria-hidden="true"></i>{d.name || "My Website"}
                    </a>
                    <button
                        className="navbar-toggler border-0"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#navbarContent"
                        aria-controls="navbarContent"
                        aria-expanded={menuOpen}
                        aria-label="Toggle navigation menu"
                        onClick={() => setMenuOpen((o) => !o)}
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className="collapse navbar-collapse" id="navbarContent">
                        <ul className="navbar-nav mx-auto mb-2 mb-lg-0 fw-semibold">
                            <li className="nav-item"><a className="nav-link px-3" href="#portfolio">Portfolio</a></li>
                            <li className="nav-item"><a className="nav-link px-3" href="#about">About</a></li>
                            <li className="nav-item"><a className="nav-link px-3" href="#services">What I Do</a></li>
                            <li className="nav-item"><a className="nav-link px-3" href="#reviews">Reviews</a></li>
                        </ul>
                        <a href="#contact" className="btn btn-dark rounded-pill px-4 py-2 fw-semibold d-none d-lg-inline-block">Get in Touch</a>
                    </div>
                </div>
            </nav>

            <main id="main-content">
                {/* HERO — background image (coverimg) + framed profile photo (aboutimg) */}
                <header
                    id="top"
                    className={`hero-section ${cover && coverOk ? "" : "hero-section-fallback"}`}
                    style={cover && coverOk ? { backgroundImage: `linear-gradient(120deg, rgba(251,247,238,.97) 0%, rgba(251,247,238,.92) 55%, rgba(251,247,238,.80) 100%), url("${cover}")` } : undefined}
                >
                    <div className="hero-blob hero-blob-1" aria-hidden="true"></div>
                    <div className="hero-blob hero-blob-2" aria-hidden="true"></div>
                    <div className="container">
                        <div className="row align-items-center g-5">
                            <div className="col-lg-7" data-reveal="fade-up">
                                <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
                                    <span className="pw-eyebrow-chip"><i className="bi bi-patch-check-fill me-1" aria-hidden="true"></i> Verified · Dial Nepal</span>
                                    {d.currentjob && <span className="pw-role-tag">{d.currentjob}</span>}
                                </div>
                                <h1 className="display-3 fw-bold mb-4">{d.name ? <>Hi, I'm<br /><span className="pw-name-accent">{firstName}.</span></> : "Welcome to my website"}</h1>
                                {d.slogan && <p className="lead pw-hero-lead mb-5 pe-lg-5">{d.slogan}</p>}
                                <div className="d-flex gap-3 flex-wrap">
                                    <a href="#portfolio" className="btn pw-btn-accent btn-lg rounded-pill px-4 py-3 fw-bold">View My Work</a>
                                    <a href="#contact" className="btn pw-btn-outline-dark btn-lg rounded-pill px-4 py-3 fw-bold">Contact Me</a>
                                </div>
                            </div>
                            <div className="col-lg-5 text-center" data-reveal="fade-left">
                                <div className="hero-photo-wrap mx-auto">
                                    <div className="hero-photo-backdrop" aria-hidden="true"></div>
                                    <div className={`hero-photo ${hasPhoto ? "" : "hero-photo-ph"}`}>
                                        {hasPhoto ? (
                                            <img src={photo} alt={d.name || "Profile"} loading="lazy" onError={() => setImgOk(false)} />
                                        ) : (
                                            <span aria-hidden="true">{initials}</span>
                                        )}
                                    </div>
                                    {(d.experience || d.currentjob) && (
                                        <div className="hero-photo-badge">
                                            {d.experience && <b>{d.experience}</b>}
                                            <span><i className="bi bi-patch-check-fill me-1" aria-hidden="true"></i>Verified</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* EMPTY-SITE NOTICE — shown only when literally nothing has been filled in yet */}
                {isSiteEmpty && (
                    <section className="py-5">
                        <div className="container py-5">
                            <SiteEmptyState
                                icon="bi-brush"
                                title="This website is still being set up"
                                message="Once the owner adds a profile photo, bio and portfolio highlights, they'll appear here."
                            />
                        </div>
                    </section>
                )}

                {/* 1. PORTFOLIO */}
                {images.length > 0 && (
                    <section id="portfolio" className="py-5 mt-3">
                        <div className="container py-4">
                            <div className="text-center mb-5" data-reveal="fade-up">
                                <span className="text-accent">Highlights</span>
                                <h2 className="display-5 section-title">Portfolio &amp; Moments</h2>
                                <p className="text-muted mx-auto" style={{ maxWidth: 600 }}>A showcase of my work, events and community highlights.</p>
                            </div>
                            <div className="row g-4">
                                {images.map((img, i) => (
                                    <div className="col-md-4" data-reveal="fade-up" data-reveal-delay={(i % 3 + 1) * 100} key={i}>
                                        <div className="card hover-lift bg-white h-100 border-0 rounded-4">
                                            <div className="portfolio-img-wrapper rounded-top-4">
                                                <ImgOrFallback src={imgUrl(img.link)} alt={img.title || `Highlight ${i + 1}`} icon="bi-images" />
                                                <span className="portfolio-img-overlay" aria-hidden="true"><i className="bi bi-arrows-fullscreen"></i></span>
                                            </div>
                                            <div className="card-body p-4">
                                                {img.type && <span className="badge bg-light text-dark mb-2">{img.type}</span>}
                                                <h5 className="fw-bold mb-0">{img.title || "Highlight"}</h5>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* 2. ABOUT */}
                {d.aboutme && (
                    <section id="about" className="py-5 bg-white">
                        <div className="container py-4">
                            <div className="row gx-lg-5 align-items-center">
                                <div className="col-lg-6 mb-4 mb-lg-0" data-reveal="fade-right">
                                    <div className={`about-photo ${hasPhoto ? "" : "about-photo-ph"}`}>
                                        {hasPhoto ? (
                                            <img src={photo} alt={d.name || "Profile"} loading="lazy" onError={() => setImgOk(false)} className="img-fluid rounded-4" />
                                        ) : (
                                            <span aria-hidden="true">{initials}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="col-lg-6" data-reveal="fade-left">
                                    <span className="text-accent">About Me</span>
                                    <h2 className="display-6 section-title mb-4">{d.name}</h2>
                                    <p className="lead text-muted mb-4">{d.aboutme}</p>
                                    <div className="row mt-4">
                                        <div className="col-sm-6 mb-3">
                                            <h3 className="fw-bold text-primary display-6 mb-0">{d.experience || "10+"}</h3>
                                            <span className="text-muted fw-semibold">Experience</span>
                                        </div>
                                        {langCount > 0 && (
                                            <div className="col-sm-6 mb-3">
                                                <h3 className="fw-bold text-primary display-6 mb-0">{langCount}</h3>
                                                <span className="text-muted fw-semibold">Languages Spoken</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {d.addresses && <span className="chip"><i className="bi bi-geo-alt-fill me-1" aria-hidden="true"></i>{d.addresses}</span>}
                                        {d.languages && <span className="chip"><i className="bi bi-translate me-1" aria-hidden="true"></i>{d.languages}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* 3. WHAT I DO */}
                {SERVICES.length > 0 && (
                    <section id="services" className="py-5">
                        <div className="container py-4">
                            <div className="text-center mb-5" data-reveal="fade-up">
                                <span className="text-accent">What I Do</span>
                                <h2 className="display-5 section-title">My Focus Areas</h2>
                            </div>
                            <div className="row g-4">
                                {SERVICES.map((s, i) => (
                                    <div className="col-md-4" data-reveal="fade-up" data-reveal-delay={(i + 1) * 100} key={i}>
                                        <div className="card bg-white border-0 p-4 h-100 hover-lift rounded-4">
                                            <div className={`bg-${s.tone} bg-opacity-10 text-${s.tone} p-3 rounded-3 d-inline-block mb-3`} style={{ width: "fit-content" }}>
                                                <i className={`bi ${s.icon} fs-3`} aria-hidden="true"></i>
                                            </div>
                                            <h4 className="fw-bold mb-3">{s.t}</h4>
                                            <p className="text-muted mb-0">{s.d}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* 4. REVIEWS */}
                {TESTIMONIALS.length > 0 && (
                    <section id="reviews" className="py-5 bg-white">
                        <div className="container py-4 border-top">
                            <div className="text-center mb-5" data-reveal="fade-up">
                                <span className="text-accent">Testimonials</span>
                                <h2 className="display-5 section-title">What People Say</h2>
                            </div>
                            <div className="row g-4">
                                {TESTIMONIALS.map((t, i) => (
                                    <div className="col-md-6" data-reveal="fade-up" data-reveal-delay={i * 100} key={i}>
                                        <div className="p-4 bg-light rounded-4 hover-lift h-100">
                                            <div className="text-warning mb-3" aria-hidden="true">
                                                <i className="bi bi-star-fill"></i><i className="bi bi-star-fill"></i><i className="bi bi-star-fill"></i><i className="bi bi-star-fill"></i><i className="bi bi-star-fill"></i>
                                            </div>
                                            <p className="fs-5 fst-italic">&quot;{t.q}&quot;</p>
                                            <h6 className="fw-bold mt-4 mb-0">{t.n}</h6>
                                            <small className="text-muted">{t.r}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* 5. CONTACT */}
                <section id="contact" className="py-5 bg-white">
                    <div className="container py-4 border-top">
                        <div className="row g-5">
                            <div className="col-lg-5" data-reveal="fade-right">
                                <span className="text-accent">Get in Touch</span>
                                <h2 className="display-6 section-title mb-4">Contact Information</h2>
                                <p className="text-muted mb-5">Reach out to connect, collaborate or simply say hello. I usually respond within a few hours on business days.</p>
                                {!d.addresses && !d.email && !d.mobile && !d.whatsapp ? (
                                    <SiteEmptyState icon="bi-person-lines-fill" title="No contact details yet" message="Contact details will appear here once added." />
                                ) : (
                                    <ul className="list-unstyled mb-0">
                                        {d.addresses && (
                                            <li className="d-flex mb-4 align-items-center pw-contact-row">
                                                <div className="pw-contact-ic text-primary"><i className="bi bi-geo-alt-fill fs-5" aria-hidden="true"></i></div>
                                                <div><h6 className="fw-bold mb-1">Location</h6><span className="text-muted">{d.addresses}</span></div>
                                            </li>
                                        )}
                                        {d.email && (
                                            <li className="d-flex mb-4 align-items-center pw-contact-row">
                                                <div className="pw-contact-ic text-primary"><i className="bi bi-envelope-fill fs-5" aria-hidden="true"></i></div>
                                                <div><h6 className="fw-bold mb-1">Email</h6><a href={`mailto:${d.email}`} className="text-muted text-decoration-none">{d.email}</a></div>
                                            </li>
                                        )}
                                        {d.mobile && (
                                            <li className="d-flex mb-4 align-items-center pw-contact-row">
                                                <div className="pw-contact-ic text-primary"><i className="bi bi-telephone-fill fs-5" aria-hidden="true"></i></div>
                                                <div><h6 className="fw-bold mb-1">Call</h6><a href={`tel:${d.mobile}`} className="text-muted text-decoration-none fw-bold">{d.mobile}</a></div>
                                            </li>
                                        )}
                                        {d.whatsapp && (
                                            <li className="d-flex align-items-center pw-contact-row">
                                                <div className="pw-contact-ic text-success"><i className="bi bi-whatsapp fs-5" aria-hidden="true"></i></div>
                                                <div><h6 className="fw-bold mb-1">WhatsApp</h6><a href={`https://wa.me/${d.whatsapp}`} target="_blank" rel="noreferrer" className="text-muted text-decoration-none fw-bold">{d.whatsapp}</a></div>
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </div>
                            <div className="col-lg-7" data-reveal="fade-left">
                                <div className="card border-0 rounded-4 bg-light p-4 p-lg-5 pw-contact-card">
                                    <h4 className="fw-bold mb-4">Send a Message</h4>
                                    <form onSubmit={handleContact} aria-label="Contact form">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold" htmlFor="pw-cname">Full Name</label>
                                                <input id="pw-cname" type="text" className="form-control form-control-lg" value={cName} onChange={(e) => setCName(e.target.value)} required />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label fw-semibold" htmlFor="pw-cphone">Phone Number</label>
                                                <input id="pw-cphone" type="tel" className="form-control form-control-lg" value={cPhone} onChange={(e) => setCPhone(e.target.value)} required />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fw-semibold" htmlFor="pw-cemail">Email Address</label>
                                                <input id="pw-cemail" type="email" className="form-control form-control-lg" value={cEmail} onChange={(e) => setCEmail(e.target.value)} required />
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label fw-semibold" htmlFor="pw-cmsg">Message</label>
                                                <textarea id="pw-cmsg" className="form-control form-control-lg" rows="4" value={cMsg} onChange={(e) => setCMsg(e.target.value)} required></textarea>
                                            </div>
                                            <div className="col-12 mt-4">
                                                <button type="submit" className="btn btn-primary btn-lg w-100 fw-bold rounded-pill">Send Message</button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* FOOTER */}
            <footer className="bg-dark text-light py-5 border-top border-secondary mt-5">
                <div className="container py-4">
                    <div className="row g-5">
                        <div className="col-lg-5 col-md-6">
                            <span className="text-white fw-bold fs-4 mb-3 d-block">
                                <i className="bi bi-patch-check-fill text-primary me-2" aria-hidden="true"></i>{d.name || "My Website"}
                            </span>
                            <p className="text-secondary mb-4">{d.slogan || d.aboutme}</p>
                            <div className="d-flex gap-3 flex-wrap">
                                {d.mobile && <a href={`tel:${d.mobile}`} className="pw-social-ic" aria-label="Call"><i className="bi bi-telephone-fill" aria-hidden="true"></i></a>}
                                {d.whatsapp && <a href={`https://wa.me/${d.whatsapp}`} target="_blank" rel="noreferrer" className="pw-social-ic" aria-label="WhatsApp"><i className="bi bi-whatsapp" aria-hidden="true"></i></a>}
                                {d.email && <a href={`mailto:${d.email}`} className="pw-social-ic" aria-label="Email"><i className="bi bi-envelope-fill" aria-hidden="true"></i></a>}
                            </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <h5 className="fw-bold mb-4">Quick Links</h5>
                            <ul className="list-unstyled mb-0">
                                <li className="mb-2"><a href="#portfolio" className="text-secondary text-decoration-none">Portfolio</a></li>
                                <li className="mb-2"><a href="#about" className="text-secondary text-decoration-none">About</a></li>
                                <li className="mb-2"><a href="#services" className="text-secondary text-decoration-none">What I Do</a></li>
                                <li className="mb-2"><a href="#reviews" className="text-secondary text-decoration-none">Reviews</a></li>
                            </ul>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <h5 className="fw-bold mb-4">Focus Areas</h5>
                            <ul className="list-unstyled mb-0">
                                {SERVICES.map((s, i) => (
                                    <li className="mb-2" key={i}><a href="#services" className="text-secondary text-decoration-none">{s.t}</a></li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <hr className="border-secondary my-5" />
                    <div className="row align-items-center">
                        <div className="col-md-6 text-center text-md-start mb-3 mb-md-0">
                            <p className="text-secondary small mb-0">&copy; {new Date().getFullYear()} <span>{d.name || "My Website"}</span>. All rights reserved.</p>
                        </div>
                        <div className="col-md-6 text-center text-md-end">
                            <p className="text-secondary small mb-0">Powered by <span className="text-gold fw-semibold">Dial Nepal</span></p>
                        </div>
                    </div>
                </div>
            </footer>

            {/* THEME OVERRIDES — cream + deep forest-green + mustard-yellow, AK-Design-inspired */}
            <style>{`
                .pw{
                    --pw-ink:#152420; --pw-forest:#173b2c; --pw-forest-d:#0e2419; --pw-muted:#6b7268;
                    --pw-cream:#fbf7ee; --pw-cream-2:#f3ecdc; --pw-gold:#f0b429; --pw-gold-d:#d99a12;
                    font-family:'Inter',sans-serif; color:var(--pw-ink); background:var(--pw-cream);
                }
                .pw h1,.pw h2,.pw h3,.pw h4,.pw .navbar-brand{ font-family:'Poppins','Inter',sans-serif; }
                .pw *:focus-visible{ outline:3px solid var(--pw-gold); outline-offset:2px; border-radius:4px; }
                .pw a, .pw button{ transition:all .2s ease; }

                .pw-skip-link{ position:absolute; left:-9999px; top:auto; z-index:2000; background:var(--pw-forest-d); color:#fff; padding:10px 18px; border-radius:0 0 8px 0; }
                .pw-skip-link:focus{ left:0; top:0; }

                .pw .text-primary{ color:var(--pw-forest) !important; }
                .pw .btn-primary{ --bs-btn-bg:var(--pw-forest); --bs-btn-border-color:var(--pw-forest); --bs-btn-hover-bg:var(--pw-forest-d); --bs-btn-hover-border-color:var(--pw-forest-d); --bs-btn-active-bg:var(--pw-forest-d); --bs-btn-active-border-color:var(--pw-forest-d); }
                .pw .btn-primary:hover{ transform:translateY(-2px); box-shadow:0 10px 22px rgba(23,59,44,.25); }
                .pw .bg-primary{ background-color:var(--pw-forest) !important; }
                .pw .text-gold{ color:var(--pw-gold-d) !important; }

                /* NAV — transparent over hero, solid + blurred once scrolled */
                .pw .navbar{ background-color:transparent; border-bottom:1px solid transparent; transition:background-color .25s ease, box-shadow .25s ease, border-color .25s ease; }
                .pw .navbar.pw-nav-scrolled{ background-color:rgba(251,247,238,.92) !important; backdrop-filter:blur(10px); border-bottom:1px solid #ece3cf; box-shadow:0 6px 24px rgba(23,59,44,.06); }
                .pw .nav-link{ color:var(--pw-ink); position:relative; }
                .pw .nav-link:after{ content:""; position:absolute; left:12px; right:12px; bottom:2px; height:2px; background:var(--pw-gold); transform:scaleX(0); transform-origin:left; transition:transform .2s ease; }
                .pw .nav-link:hover, .pw .nav-link:focus-visible{ color:var(--pw-forest); }
                .pw .nav-link:hover:after, .pw .nav-link:focus-visible:after{ transform:scaleX(1); }
                .pw .btn-dark{ --bs-btn-bg:var(--pw-forest-d); --bs-btn-border-color:var(--pw-forest-d); --bs-btn-hover-bg:var(--pw-forest); --bs-btn-hover-border-color:var(--pw-forest); }
                .pw .btn-dark:hover{ transform:translateY(-2px); box-shadow:0 10px 22px rgba(23,59,44,.25); }
                .pw .navbar-collapse.collapsing, .pw .navbar-collapse.show{ animation:pw-menu-in .25s ease; }
                @keyframes pw-menu-in{ from{ opacity:0; transform:translateY(-8px);} to{ opacity:1; transform:none; } }

                /* HERO — light cream backdrop, dark forest headline, mustard accents (AK Design style) */
                .pw .hero-section{
                    position:relative; overflow:hidden;
                    background-color:var(--pw-cream);
                    background-image:radial-gradient(rgba(23,59,44,.05) 1.5px, transparent 1.5px);
                    background-size:22px 22px;
                    background-position:center; background-repeat:repeat;
                    color:var(--pw-ink); padding:132px 0 90px 0;
                }
                .pw .hero-section-fallback{
                    background-image:
                        radial-gradient(rgba(23,59,44,.06) 1.5px, transparent 1.5px),
                        linear-gradient(135deg, var(--pw-cream) 0%, var(--pw-cream-2) 60%, #f7ecd2 100%);
                    background-size:22px 22px, cover;
                }
                .pw .hero-blob{ position:absolute; border-radius:50%; filter:blur(60px); opacity:.35; pointer-events:none; z-index:0; }
                .pw .hero-blob-1{ width:340px; height:340px; background:var(--pw-gold); top:-120px; right:-80px; }
                .pw .hero-blob-2{ width:260px; height:260px; background:var(--pw-forest); bottom:-100px; left:-60px; opacity:.12; }
                .pw .hero-section .container{ position:relative; z-index:1; }
                .pw .hero-section h1{ color:var(--pw-forest-d); }
                .pw .pw-name-accent{ color:var(--pw-forest); }
                .pw .pw-eyebrow-chip{ display:inline-flex; align-items:center; background:#e9f2ec; color:var(--pw-forest); border:1px solid #cfe3d7; font-weight:700; font-size:.8rem; padding:7px 16px; border-radius:50px; }
                .pw .pw-role-tag{ color:var(--pw-muted); font-weight:500; }
                .pw .pw-hero-lead{ color:#3f4a44; }
                .pw .pw-btn-accent{ background:var(--pw-gold); border-color:var(--pw-gold); color:var(--pw-forest-d); }
                .pw .pw-btn-accent:hover{ background:var(--pw-gold-d); border-color:var(--pw-gold-d); color:var(--pw-forest-d); transform:translateY(-3px); box-shadow:0 14px 26px rgba(240,180,41,.35); }
                .pw .pw-btn-outline-dark{ background:transparent; border:2px solid var(--pw-forest-d); color:var(--pw-forest-d); }
                .pw .pw-btn-outline-dark:hover{ background:var(--pw-forest-d); color:#fff; transform:translateY(-3px); box-shadow:0 14px 26px rgba(23,59,44,.22); }

                .pw .hero-photo-wrap{ position:relative; display:inline-block; margin-top:8px; }
                .pw .hero-photo-backdrop{ position:absolute; inset:6% 4% 4% 6%; background:var(--pw-gold); border-radius:50%; z-index:0; animation:pw-blob-pulse 6s ease-in-out infinite; }
                @keyframes pw-blob-pulse{ 0%,100%{ transform:scale(1); } 50%{ transform:scale(1.03); } }
                .pw .hero-photo{ position:relative; z-index:1; width:300px; height:300px; border-radius:50%; overflow:hidden; border:6px solid var(--pw-cream); box-shadow:0 20px 45px rgba(23,59,44,.18); background:linear-gradient(135deg,var(--pw-forest),var(--pw-forest-d)); transition:transform .35s ease; }
                .pw .hero-photo-wrap:hover .hero-photo{ transform:translateY(-4px); }
                .pw .hero-photo img{ width:100%; height:100%; object-fit:cover; }
                .pw .hero-photo-ph{ display:flex; align-items:center; justify-content:center; }
                .pw .hero-photo-ph span{ font-family:'Poppins',sans-serif; font-size:104px; font-weight:800; color:#fff; }
                .pw .hero-photo-badge{ position:relative; z-index:1; margin-top:14px; background:#fff; border-radius:14px; padding:10px 18px; box-shadow:0 10px 25px rgba(23,59,44,.14); display:inline-flex; flex-direction:column; align-items:center; gap:2px; transition:transform .25s ease; }
                .pw .hero-photo-wrap:hover .hero-photo-badge{ transform:translateY(-3px); }
                .pw .hero-photo-badge b{ color:var(--pw-forest-d); font-size:1.1rem; }
                .pw .hero-photo-badge span{ color:var(--pw-forest); font-size:.8rem; font-weight:600; }
                @media(max-width:991px){ .pw .hero-photo{ width:230px; height:230px; } }
                @media(max-width:575px){ .pw .hero-section{ padding:108px 0 64px 0; } .pw .hero-photo{ width:180px; height:180px; } }

                .pw .section-title{ font-weight:800; color:var(--pw-forest-d); margin-bottom:1rem; }
                .pw .text-accent{ color:var(--pw-gold-d); font-weight:700; letter-spacing:1px; text-transform:uppercase; font-size:.85rem; }

                /* Gradient fallback tile — themed, used any time an image is missing or fails to load */
                .pw-img-fallback{
                    width:100%; height:100%; min-height:160px; display:flex; align-items:center; justify-content:center;
                    background:linear-gradient(135deg, var(--pw-forest) 0%, var(--pw-forest-d) 55%, #0a1c14 100%);
                    background-size:200% 200%; animation:pw-fallback-shift 6s ease infinite;
                    color:var(--pw-gold); font-size:2.6rem; position:relative; overflow:hidden;
                }
                .pw-img-fallback:before{ content:""; position:absolute; inset:0; background-image:radial-gradient(rgba(240,180,41,.18) 1.5px, transparent 1.5px); background-size:16px 16px; }
                .pw-img-fallback i{ position:relative; z-index:1; opacity:.85; }
                @keyframes pw-fallback-shift{ 0%{ background-position:0% 50%; } 50%{ background-position:100% 50%; } 100%{ background-position:0% 50%; } }

                .pw .hover-lift{ transition:transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s ease; box-shadow:0 2px 10px rgba(23,59,44,.06); }
                .pw .hover-lift:hover, .pw .hover-lift:focus-within{ transform:translateY(-6px); box-shadow:0 20px 34px rgba(23,59,44,.14) !important; }

                .pw .portfolio-img-wrapper{ overflow:hidden; height:260px; position:relative; }
                .pw .portfolio-img-wrapper img{ transition:transform .5s ease; width:100%; height:100%; object-fit:cover; }
                .pw .hover-lift:hover .portfolio-img-wrapper img{ transform:scale(1.08); }
                .pw .portfolio-img-overlay{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; background:rgba(14,36,25,0); color:#fff; font-size:1.4rem; opacity:0; transition:all .3s ease; }
                .pw .hover-lift:hover .portfolio-img-overlay{ background:rgba(14,36,25,.28); opacity:1; }

                .pw .about-photo{ height:440px; border-radius:1rem; overflow:hidden; box-shadow:0 15px 35px rgba(23,59,44,.15); }
                .pw .about-photo img{ width:100%; height:100%; object-fit:cover; }
                .pw .about-photo-ph{ display:flex; align-items:center; justify-content:center; background:linear-gradient(135deg,var(--pw-forest),var(--pw-forest-d)); color:#fff; }
                .pw .about-photo-ph span{ font-family:'Poppins',sans-serif; font-size:120px; font-weight:800; }

                .pw .chip{ background:#fbf1da; color:var(--pw-gold-d); font-weight:700; padding:8px 16px; border-radius:50px; display:inline-block; border:1px solid #f0dfb0; font-size:.9rem; transition:transform .2s ease; }
                .pw .chip:hover{ transform:translateY(-2px); }

                .pw section#about .bg-light, .pw section#contact .bg-light{ background:var(--pw-cream-2) !important; }
                .pw .card.bg-light{ background:var(--pw-cream-2) !important; }
                .pw .pw-contact-card{ box-shadow:0 10px 30px rgba(23,59,44,.08); }
                .pw .pw-contact-row{ transition:transform .2s ease; }
                .pw .pw-contact-row:hover{ transform:translateX(4px); }
                .pw .pw-contact-ic{ background:var(--pw-cream-2); padding:1rem; border-radius:50%; margin-right:1rem; }

                .pw .pw-social-ic{ width:42px; height:42px; border-radius:50%; background:rgba(255,255,255,.08); color:#cfd8d1; display:inline-flex; align-items:center; justify-content:center; font-size:1.05rem; text-decoration:none; }
                .pw .pw-social-ic:hover{ background:var(--pw-gold); color:var(--pw-forest-d); transform:translateY(-3px); }

                .pw .btn:focus-visible, .pw .form-control:focus{ box-shadow:0 0 0 4px rgba(240,180,41,.25); border-color:var(--pw-gold); }
                .pw .form-control{ transition:border-color .2s ease, box-shadow .2s ease; }
            `}</style>
        </div>
    );
}